//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import { InsightsAskAIMessages } from '@insights/insightsConstants';

import {
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/metrics', '/insights');
  urlRouterProvider.when('/metrics/fanreel', '/insights/galleries');
  urlRouterProvider.when(
    '/metrics/fanreel/:pageType/:pageSource',
    '/insights/galleries/:pageType/:pageSource',
  );
  urlRouterProvider.when('/metrics/galleries', '/insights/galleries');
  urlRouterProvider.when(
    '/metrics/galleries/:pageType/:pageSource',
    '/insights/galleries/:pageType/:pageSource',
  );
  urlRouterProvider.when('/metrics/summary/export', '/insights/summary/export');
  urlRouterProvider.when('/metrics/*path', '/insights/{path}');

  /**
      Insights summary and Like2buy paths:
      - `/insights/summary` Insights summary path
      - `/insights/like2buy/summary` Like2buy summary path
      - `/insights/like2buy` Like2buy Instagram path
      - `/insights/like2buy/x888` Like2buy Tiktok path
   */
  const isInstagramEnabled = hasFeature(FeatureNames.Like2Buy);
  const isTiktokEnabled = hasFeature(FeatureNames.Like2BuyTiktok);

  // If both Like2Buy Instagram and TikTok are not enabled we will redirect the user from the Like2Buy summary path to the Insights summary path
  if (!isInstagramEnabled && !isTiktokEnabled) {
    urlRouterProvider.when('/insights/like2buy/summary', '/insights/summary');
  }

  // If Like2Buy Instagram is not enabled we will redirect the user from the Like2Buy Instagram path to the Like2Buy summary path
  if (!isInstagramEnabled) {
    urlRouterProvider.when('/insights/like2buy', '/insights/like2buy/summary');
  }

  // If Like2Buy TikTok is not enabled we will redirect the user from the Like2Buy TikTok path to the Like2Buy summary path
  if (!isTiktokEnabled) {
    urlRouterProvider.when(
      /\/insights\/like2buy\/(?!summary$).+/,
      '/insights/like2buy/summary',
    );
  }
}

const getGalleriesTitle = () => {
  if (hasFeature('galleries')) {
    return 'Gallery Insights';
  } else if (hasFeature('instashop')) {
    return 'InstaShop Insights';
  } else {
    return 'Fanreel Insights';
  }
};

const hasGalleriesAccess = () => {
  return (
    hasFeature('galleries') ||
    hasFeature('instashop') ||
    hasFeature(FeatureNames.BazaarvoiceNetworkSyndicationFrontend)
  );
};

export const states = {
  insights: StateFactory({
    url: '/insights',
    template: componentNameToTemplate('insightsPage'),
    redirectTo: 'insights.summary',
    data: {
      title: 'Insights',
      navGroup: NavGroup.INSIGHTS,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.insights',
        () => import('app/insights/InsightsApp'),
      ),
    },
  }),
  'insights.summary': StateFactory({
    url: '/summary',
    views: {
      'insights-header': {
        template: `<insights-header title="Vibe Commerce Insights" ask-ai-message="${InsightsAskAIMessages.ROI}"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('summaryInsightsPage'),
      },
    },
    data: {
      title: 'Summary Insights',
      navGroup: NavGroup.INSIGHTS,
    },
  }),
  'insights.galleries': StateFactory({
    url: '/galleries',
    views: {
      'insights-header': {
        template: componentNameToTemplate('galleryInsightsHeader'),
      },
      'insights-content': {
        template: componentNameToTemplate('galleryInsightsPage'),
      },
    },
    data: {
      title: () => getGalleriesTitle(),
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasGalleriesAccess(),
    },
  }),
  'insights.galleries-detail': StateFactory({
    url: '/galleries/:pageType/:pageSource',
    views: {
      'insights-header': {
        template: componentNameToTemplate('galleryInsightsHeader'),
      },
      'insights-content': {
        template: componentNameToTemplate('galleryInsightsDetailsPage'),
      },
    },
    data: {
      title: getGalleriesTitle(),
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasGalleriesAccess(),
    },
  }),
  'insights.like2BuySummary': StateFactory({
    url: '/like2buy/summary',
    views: {
      'insights-header': {
        template: `<insights-header title="Like2Buy Insights" ask-ai-message="${InsightsAskAIMessages.Like2Buy}"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('like2BuySummaryInsightsPage'),
      },
    },
    data: {
      title: 'Like2Buy Insights',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures('social_dashboards'),
    },
  }),
  'insights.like2buy': StateFactory({
    url: '/like2buy',
    views: {
      'insights-header': {
        template: `<insights-header title="Instagram Like2Buy Insights" ask-ai-message="${InsightsAskAIMessages.Like2BuyInstagram}"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('like2BuyInsightsPage'),
      },
    },
    data: {
      title: 'Like2Buy Insights',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures('social_dashboards'),
    },
  }),
  'insights.like2BuyV2': StateFactory({
    url: '/like2buy/:storeId',
    views: {
      'insights-header': {
        template: componentNameToTemplate('like2BuyV2InsightsHeader'),
      },
      'insights-content': {
        template: componentNameToTemplate('like2BuyV2InsightsPage'),
      },
    },
    data: {
      title: 'Like2Buy Insights',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures('social_dashboards'),
    },
  }),
  'insights.reveal': StateFactory({
    url: '/reveal',
    views: {
      'insights-header': {
        template: `<insights-header title="Reveal Insights" ask-ai-message="${InsightsAskAIMessages.Reveal}"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('revealInsightsPage'),
      },
    },
    data: {
      title: 'Reveal Insights',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures(FeatureNames.RevealPage),
    },
  }),
  'insights.rights-requests': StateFactory({
    url: '/rights-requests',
    views: {
      'insights-header': {
        template: `<insights-header title="Rights Requests Insights"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('rightsRequestsInsightsPage'),
      },
    },
    data: {
      title: 'Rights Requests',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures(FeatureNames.RightsInsights),
    },
  }),
  'insights.showroom': StateFactory({
    url: '/showroom',
    views: {
      'insights-header': {
        template: `<insights-header title="Showroom Insights" ask-ai-message="${InsightsAskAIMessages.Showroom}"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('showroomInsightsPage'),
      },
    },
    data: {
      title: 'Showroom Insights',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () => hasAllFeatures('tributary_self_serve'),
    },
  }),
  'insights.syndication': StateFactory({
    url: '/bazaarvoice-network',
    views: {
      'insights-header': {
        template: `<insights-header title="Visual Syndication Network"></insights-header>`,
      },
      'insights-content': {
        template: componentNameToTemplate('syndicationInsights'),
      },
    },
    data: {
      title: 'Insights - Visual Syndication Network',
      navGroup: NavGroup.INSIGHTS,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.BazaarvoiceNetworkSyndicationFrontend),
    },
  }),
};
